import { FormEvent, useState } from 'react';

export type Usage = 'interieur' | 'exterieur' | 'mixte';

export interface ContactValues {
  universe: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  dimensions: string;
  usage: Usage | '';
  deadline: string;
  message: string;
}

export type ContactErrors = Partial<Record<keyof ContactValues, string>>;
export type ContactStatus = 'idle' | 'submitting' | 'success' | 'error';

const initialValues: ContactValues = {
  universe: '',
  name: '',
  company: '',
  email: '',
  phone: '',
  dimensions: '',
  usage: '',
  deadline: '',
  message: '',
};

export const ACCEPTED_EXTENSIONS = ['pdf', 'step', 'stp', 'obj', 'stl', 'dwg', 'dxf', 'zip', 'jpg', 'jpeg', 'png'];
const MAX_FILES = 5;
const MAX_SIZE = 50 * 1024 * 1024;

export function useContactForm() {
  const [values, setValues] = useState<ContactValues>(initialValues);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [status, setStatus] = useState<ContactStatus>('idle');
  const [ticket, setTicket] = useState('');

  const setField = <K extends keyof ContactValues>(key: K, value: ContactValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const addFiles = (incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    const rejected: string[] = [];
    const accepted = list.filter((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        rejected.push(`${f.name} : format non accepté`);
        return false;
      }
      if (f.size > MAX_SIZE) {
        rejected.push(`${f.name} : dépasse 50 Mo`);
        return false;
      }
      return true;
    });
    setFiles((prev) => {
      const merged = [...prev, ...accepted.filter((a) => !prev.some((p) => p.name === a.name))];
      if (merged.length > MAX_FILES) rejected.push(`${MAX_FILES} fichiers maximum`);
      return merged.slice(0, MAX_FILES);
    });
    setFileError(rejected.join(' · '));
  };

  const removeFile = (name: string) => setFiles((prev) => prev.filter((f) => f.name !== name));

  const validate = (): ContactErrors => {
    const e: ContactErrors = {};
    if (!values.universe) e.universe = 'Sélectionnez un type de projet.';
    if (!values.name.trim()) e.name = 'Indiquez votre nom.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = 'Adresse e-mail invalide.';
    if (values.message.trim().length < 20) e.message = 'Décrivez votre projet en quelques lignes (20 caractères min.).';
    return e;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStatus('submitting');
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 1400));
      setTicket(`AV-${Date.now().toString().slice(-6)}`);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => {
    setValues(initialValues);
    setFiles([]);
    setErrors({});
    setFileError('');
    setStatus('idle');
  };

  return { values, errors, files, fileError, status, ticket, setField, addFiles, removeFile, submit, reset };
}
